import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  Card,
  Divider,
  InputLabel,
  Alert,
  Snackbar,
  useMediaQuery,
  CircularProgress,
  MenuItem,
  FormControl,
  Select,
} from "@mui/material";
import Sidenav from "../../common/Sidenav";
import Header from "../../common/Header";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { DropzoneArea } from "mui-file-dropzone";
import { config } from "../../../config/config";
import { apiList, invokeApi } from "../../../services/apiServices";
import { useCookies } from "react-cookie";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import IconSidenav from "../../common/IconSidenav";

const EditPhotos = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // Get photo ID from URL params
  const photoDataFromState = location.state?.photoData;

  const [photoDetails, setPhotoDetails] = useState({
    featuredImage: "",
    status: "",
  });
  
  const [cookies] = useCookies();
  const [photoImage, setPhotoImage] = useState(null);
  const [selectedPhotoImage, setSelectedPhotoImage] = useState("");
  const [photoImageChanged, setPhotoImageChanged] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");
  const [showSideNav, setShowSideNav] = useState(false);
  const isMobileScreen = useMediaQuery(
    "(min-width:360px) and (max-width:500px)"
  );
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!photoDataFromState);
  
  // Status options
  const statusOptions = [
    "Active",
    "Inactive",
    "Draft"
  ];

  const toggleSideNav = () => {
    setShowSideNav(!showSideNav);
  };

  // Fetch photo data if not passed via state
  useEffect(() => {
    const fetchPhotoData = async () => {
      if (!photoDataFromState && id) {
        try {
          setFetching(true);
          const params = { id: id };
          const response = await invokeApi(
            config.getMyCollege + apiList.getPhoto,
            params,
            cookies
          );

          if (response?.status === 200) {
            const photoData = response.data.photo;
            setPhotoDetails({
              featuredImage: photoData.featuredImage || photoData.featured_image || "",
              status: photoData.status || "",
            });
            setSelectedPhotoImage(photoData.featuredImage || photoData.featured_image || "");
          } else {
            showSnackbar("Failed to fetch photo data", "error");
            navigate("/photo-manager/photo-list");
          }
        } catch (error) {
          console.error("Error fetching photo data:", error);
          showSnackbar("Error fetching photo data", "error");
          navigate("/photo-manager/photo-list");
        } finally {
          setFetching(false);
        }
      } else if (photoDataFromState) {
        // Use data from state if available
        setPhotoDetails({
          featuredImage: photoDataFromState.featuredImage || photoDataFromState.featured_image || "",
          status: photoDataFromState.status || "",
        });
        setSelectedPhotoImage(photoDataFromState.featuredImage || photoDataFromState.featured_image || "");
      }
    };

    fetchPhotoData();
  }, [id, photoDataFromState, cookies, navigate]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setPhotoDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handlePhotoImage = (files) => {
    if (files && files.length > 0) {
      setPhotoImageChanged(true);
      const selectedPhotoFile = files[0];
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedPhotoImage(reader.result);
        setPhotoImage(selectedPhotoFile);
      };
      reader.readAsDataURL(selectedPhotoFile);
    } else {
      setSelectedPhotoImage("");
      setPhotoImage(null);
      setPhotoImageChanged(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleAddPhotoImage = async () => {
    if (!photoImage) return photoDetails.featuredImage;

    const formData = new FormData();
    formData.append("image", photoImage);
    try {
      const response = await fetch(
        "https://api.reef.org.in/addPhotoImage",
        {
          method: "POST",
          body: formData,
        }
      );

      if (response?.status >= 200 && response?.status < 300) {
        const responseData = await response.json();
        return responseData.imageUrl || "";
      } else {
        showSnackbar("Failed to add photo image", "error");
        return photoDetails.featuredImage;
      }
    } catch (error) {
      showSnackbar("Something went wrong. Please try again later.", "error");
      return photoDetails.featuredImage;
    }
  };

  const handleUpdatePhoto = async (updatedPhotoData) => {
    try {
      setLoading(true);
      
      const response = await invokeApi(
        config.getMyCollege + apiList.updatePhoto,
        updatedPhotoData,
        cookies
      );

      // If we get any 2xx response, consider it successful
      if (response?.status >= 200 && response?.status < 300) {
        showSnackbar("Photo updated successfully", "success");
        navigate("/photo-manager/photo-list");
      } else {
        showSnackbar("Update failed. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error during updating photo:", error);
      showSnackbar("Something went wrong. Please try again later!!", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!photoDetails.featuredImage?.trim() && !photoImageChanged) {
      showSnackbar("Featured image is required", "error");
      return;
    }

    if (!photoDetails.status?.trim()) {
      showSnackbar("Status is required", "error");
      return;
    }

    try {
      const featuredImage = photoImageChanged
        ? await handleAddPhotoImage()
        : photoDetails.featuredImage;

      const updatedPhotoData = {
        id: id,
        featuredImage: featuredImage,
        status: photoDetails.status.trim(),
      };

      await handleUpdatePhoto(updatedPhotoData);

    } catch (error) {
      console.error("Error while updating photo details:", error);
      showSnackbar("Something went wrong. Please try again later.", "error");
    }
  };

  if (fetching) {
    return (
      <Grid sx={{ display: "flex" }}>
        {isMobileScreen ? <IconSidenav /> : showSideNav ? <IconSidenav /> : <Sidenav />}
        <Grid component="main" sx={{ width: "100%", px: 1 }}>
          <Header toggleSideNav={toggleSideNav} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <CircularProgress />
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <div>
      <Grid sx={{ display: "flex" }}>
        {isMobileScreen ? (
          <IconSidenav />
        ) : showSideNav ? (
          <IconSidenav />
        ) : (
          <Sidenav />
        )}
        <Grid component="main" sx={{ width: "100%", px: 1 }}>
          <Header toggleSideNav={toggleSideNav} />

          <Grid sx={{ width: "100%", px: isMobileScreen ? 1 : 5 }}>
            <Card elevation={1} sx={{ my: 5 }}>
              <Grid sx={{ py: isMobileScreen ? 2 : 2 }}>
                <Typography
                  sx={{
                    mb: 2,
                    mx: isMobileScreen ? 2 : 8,
                    textAlign: "left",
                    fontSize: isMobileScreen ? 14 : 18,
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Edit Photo
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid
                  container
                  md={12}
                  xs={12}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-around",
                    my: 2,
                  }}
                >
                  {/* Status Field */}
                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Status *
                    </InputLabel>
                    <FormControl fullWidth>
                      <Select
                        name="status"
                        value={photoDetails.status}
                        onChange={handleInputChange}
                        displayEmpty
                        inputProps={{
                          style: {
                            height: isMobileScreen ? "35px" : "40px",
                            fontSize: isMobileScreen ? 12 : 14,
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>Select Status</em>
                        </MenuItem>
                        {statusOptions.map((status) => (
                          <MenuItem key={status} value={status}>
                            {status}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Image Upload Section */}
                  <Grid
                    container
                    md={12}
                    sx={{
                      width: "100%",
                      justifyContent: "center",
                      my: 5,
                    }}
                  >
                    <Grid item md={5} xs={11}>
                      <InputLabel
                        sx={{
                          fontWeight: "bold",
                          my: 1,
                          fontSize: isMobileScreen ? 12 : 15,
                        }}
                      >
                        Featured Image *
                      </InputLabel>
                      <Grid
                        sx={{
                          my: 2,
                          ".MuiBox-root ": {
                            bgcolor: "#f0f8ff",
                            borderColor: "#1d1a57",
                            minHeight: 0,
                            my: 1,
                            alignItems: "center",
                            justifyContent: "center",
                          },
                        }}
                      >
                        <DropzoneArea
                          filesLimit={1}
                          acceptedFiles={["image/jpeg", "image/png"]}
                          onChange={handlePhotoImage}
                          maxFileSize={5000000}
                          showPreviews={true}
                          showAlerts={false}
                          showPreviewsInDropzone={false}
                          showFileNames={false}
                          Icon={(props) => (
                            <CloudUploadIcon
                              style={{
                                color: "#1d1a57",
                                width: 35,
                                height: 35,
                              }}
                              {...props}
                            />
                          )}
                          dropzoneText={
                            <Typography
                              sx={{
                                color: "#1d1a57",
                                fontWeight: 600,
                                fontSize: 16,
                              }}
                            >
                              Upload / Drag files
                            </Typography>
                          }
                          previewText="Selected Featured Image:"
                        />
                      </Grid>
                      <Grid
                        sx={{
                          my: 3,
                          py: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          height: "auto",
                        }}
                      >
                        {selectedPhotoImage && (
                          <img
                            src={selectedPhotoImage}
                            alt="FeaturedImage"
                            style={{ maxWidth: "90px" }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Divider sx={{ mb: 1 }} />
                <Grid
                  container
                  md={12}
                  xs={12}
                  sx={{
                    width: "100%",
                  }}
                >
                  <Divider sx={{ mb: 1 }} />
                  <Grid
                    container
                    spacing={isMobileScreen ? 0 : 2}
                    md={12}
                    xs={12}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mt: 2,
                    }}
                  >
                    <Button
                      color="success"
                      sx={{
                        mr: 4,
                        my: 2,
                        px: 2,
                        fontSize: 12,
                        background: "#1d1a57",
                        color: "#FFF",
                        fontWeight: 600,
                        "&:hover": {
                          background: "#1d1a57",
                          color: "#FFF",
                        },
                      }}
                      onClick={handlePhotoSubmit}
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Save Changes"}
                    </Button>
                    <Button
                      color="secondary"
                      sx={{
                        my: 2,
                        px: 2,
                        fontSize: 12,
                        background: "#6c757d",
                        color: "#FFF",
                        fontWeight: 600,
                        "&:hover": {
                          background: "#5a6268",
                          color: "#FFF",
                        },
                      }}
                      onClick={() => navigate("/photo-manager/photo-list")}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Card>
            <Snackbar
              open={openSnackbar}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "auto" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbarSeverity}
                sx={{ width: "auto" }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default EditPhotos;